import type { TOC } from '@ember/component/template-only'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import wlLogo from '@images/logos/wl-logo.svg'
import { t } from 'ember-intl'

interface StudentDetailsSignature {
  Element: HTMLDivElement
  Args: {
    studentName: string
  }
}

const SheetStudentDetails: TOC<StudentDetailsSignature> = <template>
  <div class="flex w-full flex-row items-end justify-between text-2xl italic" ...attributes>
    <img src={{wlLogo}} width="150" height="100" alt={{t "products.writing-legends"}} />
    <span class="text-3xl">{{t "reporting.pageTitles.writingLegends.spelling"}}</span>
    <span>{{t "pages.wlSpelling.studentWordsPrint.name"}}: {{@studentName}}</span>
    <span>
      {{t "pages.wlSpelling.studentWordsPrint.date"}}:
      <span class="ml-2 inline-block border-b border-black px-20"></span>
    </span>
  </div>
</template>

const SingleWordsTable: TOC<{ words: string[] }> = <template>
  <table class="w-full">
    <thead>
      <tr class="divide-dusty-black-100 border-dusty-black-100 divide-x border text-xl">
        <th class="px-3 py-2 font-normal">{{t "pages.wlSpelling.studentWordsPrint.headers.words"}}</th>
        <th class="px-3 py-2 font-normal">{{t "pages.wlSpelling.studentWordsPrint.headers.syllables"}}</th>
        <th class="px-3 py-2 font-normal">
          {{t "pages.wlSpelling.studentWordsPrint.headers.firstAttempt" htmlSafe=true}}
        </th>
        <th class="w-[5%] px-3 py-2 text-center font-normal">
          <PhosphorIcon @icon="phosphor-check-bold" />
        </th>
        <th class="px-3 py-2 font-normal">
          {{t "pages.wlSpelling.studentWordsPrint.headers.secondAttempt" htmlSafe=true}}
        </th>
        <th class="w-[5%] px-3 py-2 text-center font-normal">
          <PhosphorIcon @icon="phosphor-check-bold" />
        </th>
        <th class="px-3 py-2 font-normal">
          {{t "pages.wlSpelling.studentWordsPrint.headers.thirdAttempt" htmlSafe=true}}
        </th>
        <th class="w-[5%] px-3 py-2 text-center font-normal">
          <PhosphorIcon @icon="phosphor-check-bold" />
        </th>
      </tr>
    </thead>
    <tbody>
      {{#each @words as |word|}}
        {{#if word}}
          <tr class="border-dusty-black-100 border">
            <td class="border-dusty-black-100 border px-3 py-2 font-normal">{{word}}</td>
            {{#each (Array 7)}}
              <td class="border-dusty-black-100 border px-2 py-1"></td>
            {{/each}}
          </tr>
        {{else}}
          <tr class="border-dusty-black-100 border">
            {{#each (Array 8)}}
              <td class="border-dusty-black-100 border px-2 py-5"></td>
            {{/each}}
          </tr>
        {{/if}}
      {{/each}}
    </tbody>
  </table>
</template>

interface SentencesActivitySignature {
  Element: HTMLDivElement
}

const SentencesActivity: TOC<SentencesActivitySignature> = <template>
  <div class="border-dusty-black-100 flex flex-row border" ...attributes>
    <div class="flex w-1/5 flex-col justify-center space-y-2 px-3 py-7 text-sm">
      <span>
        {{t "pages.wlSpelling.studentWordsPrint.actionMessages.chooseWords" htmlSafe=true}}
      </span>
      <span>{{t "pages.wlSpelling.studentWordsPrint.actionMessages.underline"}}</span>
    </div>
    <div class="divide-dusty-black-100 border-dusty-black-100 w-4/5 divide-y divide-dashed border-l">
      {{#each (Array 5)}}
        <div class="py-4"></div>
      {{/each}}
    </div>
  </div>
</template>

interface Signature {
  Element: HTMLDivElement
  Args: {
    words: string[]
    studentName: string
  }
}

export const StudentWordsPrintable: TOC<Signature> = <template>
  <div class="border-dusty-black-200 border px-8 pt-5 pb-10" ...attributes>
    <SheetStudentDetails class="mb-7" @studentName={{@studentName}} />
    <SingleWordsTable @words={{@words}} />
    <SentencesActivity class="mt-4" />
  </div>
</template>
